import React, { useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { SRInput } from '@sr/ui'
import { FORM_TYPE } from '@shared/ui'
import * as yup from 'yup'

const FORM_FIELDS = {
  startDate: 'startDate', 
  endDate: 'endDate',
} as const

interface FormData {
  startDate: string
  endDate: string
}

interface TravelTimeFormProps {
  defaultValues: FormData
  onSubmit: (data: FormData) => void
  isReadOnly: boolean
  isEdit: boolean
  isEditing: boolean
  travelType: string
  appointmentStartTime: string
  appointmentEndTime: string
  currentEffectiveStart?: string
  currentEffectiveEnd?: string
}

export const TravelTimeForm: React.FC<TravelTimeFormProps> = ({
  defaultValues,
  onSubmit,
  isReadOnly,
  isEdit,
  isEditing,
  travelType,
  appointmentStartTime,
  appointmentEndTime,
  currentEffectiveStart,
  currentEffectiveEnd,
}) => {
  const { t } = useTranslation('s-referent')

  // Helper function to compare times in HH:mm format
  const isTimeBefore = (time1: string, time2: string): boolean => {
    if (!time1 || !time2) return false
    const [h1, m1] = time1.split(':').map(Number)
    const [h2, m2] = time2.split(':').map(Number)
    return h1 < h2 || (h1 === h2 && m1 < m2)
  }

  const isTimeAfter = (time1: string, time2: string): boolean => {
    if (!time1 || !time2) return false
    const [h1, m1] = time1.split(':').map(Number)
    const [h2, m2] = time2.split(':').map(Number)
    return h1 > h2 || (h1 === h2 && m1 > m2)
  }

  // Helper function to extract time from effective times (which can be Date objects or strings)
  const getTimeFromEffective = (effectiveTime: any): string => {
    if (!effectiveTime) return ''
    
    if (typeof effectiveTime === 'string' && /^\d{2}:\d{2}$/.test(effectiveTime)) {
      return effectiveTime
    }
    
    try {
      const date = new Date(effectiveTime)
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
      }
    } catch {
      // Fallback to empty string
    }
    
    return ''
  }

  const schema = useMemo(() => {
    return yup.object().shape({
      [FORM_FIELDS.startDate]: yup
        .string()
        .when([], {
          is: () => travelType === 'before',
          then: (schema) => schema
            .required(t('agenda.formModal.formError.startDate'))
            .test(
              'not-after-appointment',
              t('agenda.travelTime.validation.startTimeAfterAppointment', { time: appointmentStartTime }),
              function(value) {
                if (!value || !appointmentStartTime) return true
                return isTimeBefore(value, appointmentStartTime) || value === appointmentStartTime
              }
            )
            .test(
              'not-after-effective-end',
              t('agenda.travelTime.validation.startTimeAfterEffectiveEnd'),
              function(value) {
                if (!value || !currentEffectiveEnd) return true
                const effectiveEndTime = getTimeFromEffective(currentEffectiveEnd)
                if (!effectiveEndTime) return true
                return isTimeBefore(value, effectiveEndTime) || value === effectiveEndTime
              }
            ),
          otherwise: (schema) => schema
        }),
      [FORM_FIELDS.endDate]: yup
        .string()
        .when([], {
          is: () => travelType === 'after',
          then: (schema) => schema
            .required(t('agenda.formModal.formError.endDate'))
            .test(
              'not-before-appointment',
              t('agenda.travelTime.validation.endTimeBeforeAppointment', { time: appointmentEndTime }),
              function(value) {
                if (!value || !appointmentEndTime) return true
                return isTimeAfter(value, appointmentEndTime) || value === appointmentEndTime
              }
            )
            .test(
              'not-before-effective-start',
              t('agenda.travelTime.validation.endTimeBeforeEffectiveStart'),
              function(value) {
                if (!value || !currentEffectiveStart) return true
                const effectiveStartTime = getTimeFromEffective(currentEffectiveStart)
                if (!effectiveStartTime) return true
                return isTimeAfter(value, effectiveStartTime) || value === effectiveStartTime
              }
            ),
          otherwise: (schema) => schema
        }),
    })
  }, [t, travelType, appointmentStartTime, appointmentEndTime, currentEffectiveStart, currentEffectiveEnd, isTimeBefore, isTimeAfter, getTimeFromEffective])


  const methods = useForm({
    resolver: yupResolver(schema) as any,
    defaultValues,
    mode: 'onChange',
  })


  const { handleSubmit, reset } = methods

  // Use effect to reset form values when defaultValues change
  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const onFormSubmit = handleSubmit((data: FormData) => {
    onSubmit(data)
  })

  const isBlocked = isEdit && !isEditing

  return (
    <FormProvider {...methods}>
      <form id="travel-time-form" onSubmit={onFormSubmit}>
        <div className="space-y-4">
          {travelType === 'before' ? (
            // For "before" travel: input for start time, text for end time (appointment start)
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('agenda.travelTime.travelStartTime')}
                </label>
                <SRInput
                  name={FORM_FIELDS.startDate}
                  type={FORM_TYPE.time}
                  disabled={isReadOnly || isBlocked}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('agenda.travelTime.travelEndTime')}
                </label>
                <div className="px-3 py-3 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                  {appointmentStartTime}
                </div>
              </div>
            </div>
          ) : (
            // For "after" travel: text for start time (appointment end), input for end time
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('agenda.travelTime.travelStartTime')}
                </label>
                <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                  {appointmentEndTime}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('agenda.travelTime.travelEndTime')}
                </label>
                <SRInput
                  name={FORM_FIELDS.endDate}
                  type={FORM_TYPE.time}
                  disabled={isReadOnly || isBlocked}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  )
}

